import { useState } from 'react'
import { useSQLiteQuery } from '../lib/reactive'
import {
  deleteNoteLocal,
  restoreNote,
  enqueueSyncOp,
  type LocalNote,
} from '../lib/db'
import { rowToLocalNote, type NoteRow } from '../lib/repositories'
import { query, execute, transaction, notifyChanges } from '../lib/sqlite'
import { track, AnalyticsEvent } from '../lib/analytics'

export function useTrashNotes(): LocalNote[] {
  return useSQLiteQuery(
    () => {
      const rows = query<NoteRow>('SELECT * FROM notes WHERE deleted_at IS NOT NULL ORDER BY updated_at DESC')
      return rows.map(rowToLocalNote)
    },
    [],
    ['notes'],
    [] as LocalNote[],
  ) ?? []
}

export function useRestoreNote() {
  const [isPending, setIsPending] = useState(false)

  const mutate = async (id: string, source = 'trash_view'): Promise<void> => {
    setIsPending(true)
    try {
      restoreNote(id)
      enqueueSyncOp({
        type: 'restore',
        entity: 'note',
        entityId: id,
        timestamp: Date.now(),
        retries: 0,
      })
      track(AnalyticsEvent.NoteAction, { action: 'restore', source })
    } finally {
      setIsPending(false)
    }
  }

  return { mutate, isPending }
}

export function useDeletePermanently() {
  const [isPending, setIsPending] = useState(false)

  const mutate = async (id: string, source = 'trash_view'): Promise<void> => {
    setIsPending(true)
    try {
      deleteNoteLocal(id)
      enqueueSyncOp({
        type: 'delete',
        entity: 'note',
        entityId: id,
        timestamp: Date.now(),
        retries: 0,
      })
      track(AnalyticsEvent.NoteAction, { action: 'delete_permanent', source })
    } finally {
      setIsPending(false)
    }
  }

  return { mutate, isPending }
}

export function useEmptyTrash() {
  const [isPending, setIsPending] = useState(false)

  const mutate = async (): Promise<void> => {
    setIsPending(true)
    try {
      const trashedNotes = query<{ id: string }>('SELECT id FROM notes WHERE deleted_at IS NOT NULL')
      transaction(() => {
        for (const note of trashedNotes) {
          execute('DELETE FROM note_tags WHERE note_id = ?', [note.id])
          execute('DELETE FROM note_links WHERE source_note_id = ? OR target_note_id = ?', [note.id, note.id])
          execute('DELETE FROM notes WHERE id = ?', [note.id])
          execute(
            'INSERT INTO sync_queue (type, entity, entity_id, timestamp, retries) VALUES (?, ?, ?, ?, ?)',
            ['delete', 'note', note.id, Date.now(), 0],
          )
        }
      })
      notifyChanges(['notes', 'note_tags', 'note_links', 'sync_queue'])
      track(AnalyticsEvent.NoteAction, {
        action: 'empty_trash',
        source: 'trash_view',
        count: trashedNotes.length,
      })
    } finally {
      setIsPending(false)
    }
  }

  return { mutate, isPending }
}
